import { faComment } from '@fortawesome/free-solid-svg-icons/faComment'
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar'
import React, { useState, useRef } from 'react'
import { Simple } from '../Avatar'
import * as Navigation from '../Navigation'
import { concatStyles, truncate } from 'utils'
import { Name } from './Name'
import { Subtitle } from './Subtitle'
import { Body } from './Body'
import { View } from './View'

export interface Profile {
  createdAt?: string
  color?: string
  avatarUrl?: string
  about: string
  keywords?: string[]
  hasUnreadMessages?: boolean
  title: string
  name: string
  id: string
}

export type FavoriteProfileHandlerFn = (
  id: string,
  favorite: boolean
) => Promise<any>

export type ProfileCardSelectFn = (
  profileId: string,
  options?: { messages?: boolean }
) => void

export interface ProfileCardProps {
  profile: Profile
  favorite?: boolean
  alert?: boolean
  onSelectProfile?: ProfileCardSelectFn
  onFavoriteProfile?: FavoriteProfileHandlerFn
  renderAccessory?: () => JSX.Element | null
  className?: string
}

const ICON_GROUP_STYLE =
  'flex absolute ml-2 mr-2 md:ml-3 md:mr-3 z-20 hover:scale-125 transition-transform duration-200' // Higher z-index to be above card overlay
const PROFILE_CARD_STYLE = 'h-auto sm:h-48 md:h-56 lg:h-64 text-center'

export const Profile: React.FC<ProfileCardProps> = ({
  favorite,
  profile,
  onSelectProfile,
  onFavoriteProfile: handleFavorite,
  renderAccessory,
  className,
}) => {
  const [favoriting, setFavoriting] = useState(false)
  const ignoreCardClick = useRef(false)

  const onSelect = (e: React.MouseEvent) => {
    // Don't trigger card click if we just clicked a favorite button
    if (ignoreCardClick.current) {
      ignoreCardClick.current = false
      return
    }

    if (onSelectProfile) {
      onSelectProfile(profile.id)
    }
  }

  const handleFavoriteClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    ignoreCardClick.current = true

    if (handleFavorite) {
      setFavoriting(true)
      await handleFavorite(profile.id, !favorite)
      setFavoriting(false)
    }
  }

  const handleMessageClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    ignoreCardClick.current = true

    if (onSelectProfile) {
      onSelectProfile(profile.id, { messages: true })
    }
  }

  return (
    <View
      className={concatStyles([PROFILE_CARD_STYLE, className])}
      onClick={onSelect}
    >
      <div className="flex flex-col my-auto flex-grow">
        <div
          className={ICON_GROUP_STYLE}
          style={{
            right: 'auto',
            left: 0,
          }}
        >
          {renderAccessory && renderAccessory()}
        </div>
        <div
          className={ICON_GROUP_STYLE}
          style={{
            left: 'auto',
            right: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {profile.hasUnreadMessages ? (
            <div className="mb-1" onClick={(e) => e.stopPropagation()}>
              <Navigation.IconButton
                appearance="accent"
                icon={faComment}
                onClick={handleMessageClick}
              />
            </div>
          ) : null}
          <div onClick={(e) => e.stopPropagation()}>
            <Navigation.IconButton
              icon={faStar}
              onClick={handleFavoriteClick}
              appearance={favorite ? 'accent' : undefined}
              loading={favoriting}
            />
          </div>
        </div>
        <div className="mx-auto mb-2">
          <Simple
            color={profile?.color ?? undefined}
            name={profile.name}
            maxInitials={2}
            imageUrl={profile.avatarUrl}
            size={100}
          />
        </div>
        <Name>{profile.name}</Name>
        <Subtitle>{profile.title}</Subtitle>
        <Body>{truncate(profile.about, 81)}</Body>
        {profile.keywords ? (
          <p className="font-body text-body text-xxs font-hairline opacity-50 lowercase mt-1">
            {profile.keywords?.join(', ')}
          </p>
        ) : null}
      </div>
    </View>
  )
}

Profile.displayName = 'Card.Profile'
